import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/race-tracker/components/ui/dialog";
import { Button } from "@/race-tracker/components/ui/button";
import { Input } from "@/race-tracker/components/ui/input";
import { Textarea } from "@/race-tracker/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/race-tracker/components/ui/select";
import { Label } from "@/race-tracker/components/ui/label";
import { Plus, Trash2, Save, Clock, Zap, Radio } from "lucide-react";
import { base44 } from "@/race-tracker/api/rtClient";
import { normalizeSerperQueryFields, normalizeSerperMode } from "@/race-tracker/lib/serperQueryCatalog";
import { format } from "date-fns";
import OpponentCard from "../cards/OpponentCard";
import DeleteConfirmDialog from "../DeleteConfirmDialog";

const CHAMBERS = ["U.S. House", "U.S. Senate", "State Senate", "State House", "Governor", "Other"];
const ELECTION_TYPES = ["Primary", "General", "Runoff", "Special"];
const GE_RATINGS = ["Safe D", "Likely D", "Lean D", "Toss Up", "Lean R", "Likely R", "Safe R"];
const STATUSES = ["Active", "Inactive", "Done"];
const STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"
];

export default function RaceDetailModal({ race, open, onClose, onSave, onDelete }) {
  const [form, setForm] = useState(race || {});
  const [opponents, setOpponents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  /** null | { kind: 'race' } | { kind: 'opponent', id, label } */
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    if (race?.id) {
      setForm({
        ...race,
        serper_query_fields: normalizeSerperQueryFields(race.serper_query_fields),
        serper_mode: normalizeSerperMode(race.serper_mode),
      });
      setDeleteTarget(null);
      loadOpponents(race.id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- reset form when race id changes
  }, [race?.id]);

  const loadOpponents = async (raceId) => {
    setLoading(true);
    const data = await base44.entities.Opponent.filter({ race_id: raceId });
    setOpponents(data);
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    const updates = { ...form };
    updates.serper_query_fields = normalizeSerperQueryFields(form.serper_query_fields);
    updates.serper_mode = normalizeSerperMode(form.serper_mode);
    if (updates.notes !== race?.notes) {
      updates.notes_updated_at = new Date().toISOString();
    }
    delete updates.id;
    delete updates.created_date;
    delete updates.updated_date;
    delete updates.created_by;
    delete updates._opponent_count;
    await base44.entities.Race.update(race.id, updates);
    onSave();
    setSaving(false);
  };

  const handleAddOpponent = async () => {
    await base44.entities.Opponent.create({ race_id: race.id, opponent_name: "New Opponent", party: "D" });
    loadOpponents(race.id);
  };

  const handleUpdateOpponent = async (id, data) => {
    await base44.entities.Opponent.update(id, data);
    loadOpponents(race.id);
  };

  const handleDeleteOpponent = async (id) => {
    await base44.entities.Opponent.delete(id);
    loadOpponents(race.id);
  };

  const update = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  const raceLabel = `${form.state || "—"} · ${form.chamber || "—"} · ${form.district || "—"}`;

  const today = format(new Date(), "yyyy-MM-dd");

  return (
    <>
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <span className="font-mono">{form.state}</span>
            <span className="text-muted-foreground">·</span>
            <span>{form.chamber}</span>
            <span className="text-muted-foreground">·</span>
            <span className="font-mono">{form.district || "—"}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          {/* Sweep Dates — TOP PRIORITY */}
          <div className="rounded-lg border border-border bg-muted/40 p-3 space-y-2">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Daily Sweep</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-1">
                  <Zap className="w-3 h-3 text-primary" /> Last Digital Sweep
                </Label>
                <div className="flex items-center gap-1.5">
                  <Input
                    type="date"
                    className="h-8 text-xs flex-1"
                    value={form.last_digital_sweep || ""}
                    onChange={(e) => update("last_digital_sweep", e.target.value)}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-[10px] px-2 shrink-0"
                    onClick={() => update("last_digital_sweep", today)}
                  >
                    Today
                  </Button>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-1">
                  <Radio className="w-3 h-3 text-primary" /> Last Linear Sweep
                </Label>
                <div className="flex items-center gap-1.5">
                  <Input
                    type="date"
                    className="h-8 text-xs flex-1"
                    value={form.last_linear_sweep || ""}
                    onChange={(e) => update("last_linear_sweep", e.target.value)}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-[10px] px-2 shrink-0"
                    onClick={() => update("last_linear_sweep", today)}
                  >
                    Today
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Core Race Fields */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">State</Label>
              <Select value={form.state || ""} onValueChange={(v) => update("state", v)}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Level of Office</Label>
              <Select value={form.chamber || ""} onValueChange={(v) => update("chamber", v)}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{CHAMBERS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">District / Race</Label>
              <Input className="h-8 text-xs" value={form.district || ""} onChange={(e) => update("district", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Team</Label>
              <Input className="h-8 text-xs" value={form.team || ""} onChange={(e) => update("team", e.target.value)} placeholder="e.g. pro-Doug Harwell" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Election Type</Label>
              <Select value={form.election_type || ""} onValueChange={(v) => update("election_type", v)}>
                <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{ELECTION_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Primary Date</Label>
              <Input type="date" className="h-8 text-xs" value={form.election_date || ""} onChange={(e) => update("election_date", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Runoff Date</Label>
              <Input type="date" className="h-8 text-xs" value={form.runoff_date || ""} onChange={(e) => update("runoff_date", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">GE Rating</Label>
              <Select value={form.ge_rating || ""} onValueChange={(v) => update("ge_rating", v)}>
                <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{GE_RATINGS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Race Importance (1–5)</Label>
              <Select value={form.race_importance ? String(form.race_importance) : ""} onValueChange={(v) => update("race_importance", Number(v))}>
                <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{[1,2,3,4,5].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          {/* Markets & Reference */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Markets</Label>
              <Input className="h-8 text-xs" value={form.markets || ""} onChange={(e) => update("markets", e.target.value)} placeholder="e.g. Mobile, Birmingham" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Reference Link</Label>
              <Input className="h-8 text-xs" value={form.reference_link || ""} onChange={(e) => update("reference_link", e.target.value)} placeholder="https://..." />
            </div>
          </div>

          {/* Intel Serper — configured in Intel Sweep */}
          <div className="rounded-md border border-dashed border-border bg-muted/20 px-3 py-2">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Intel Serper (sweep)</p>
            <p className="text-[11px] text-muted-foreground mt-1 leading-snug">
              Automatic Serper query columns and org locale are configured in{" "}
              <span className="font-medium text-foreground">Intel Sweep</span> → <span className="font-medium text-foreground">Google News</span> →{" "}
              <span className="font-medium text-foreground">Serper Query Setup</span>. Serper API key: Integrations.
            </p>
          </div>

          {/* Status */}
          <div className="space-y-1.5">
            <Label className="text-xs">Status</Label>
            <Select value={form.status || "Active"} onValueChange={(v) => update("status", v)}>
              <SelectTrigger className="h-8 text-xs w-40"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Notes</Label>
              {form.notes_updated_at && (
                <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                  <Clock className="w-3 h-3" />
                  Updated {format(new Date(form.notes_updated_at), "MMM d, yyyy h:mm a")}
                </span>
              )}
            </div>
            <Textarea
              className="text-xs min-h-[80px] resize-none"
              value={form.notes || ""}
              onChange={(e) => update("notes", e.target.value)}
              placeholder="Analyst notes..."
            />
          </div>

          {/* Opponents */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold uppercase tracking-wider">Opponents</Label>
              <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={handleAddOpponent}>
                <Plus className="w-3 h-3" /> Add Opponent
              </Button>
            </div>
            {loading ? (
              <div className="py-8 text-center text-xs text-muted-foreground">Loading...</div>
            ) : opponents.length === 0 ? (
              <div className="py-8 text-center text-xs text-muted-foreground">No opponents yet</div>
            ) : (
              <div className="space-y-2">
                {opponents.map((opp) => (
                  <OpponentCard
                    key={opp.id}
                    opponent={opp}
                    onUpdate={(data) => handleUpdateOpponent(opp.id, data)}
                    onDelete={() =>
                      setDeleteTarget({
                        kind: "opponent",
                        id: opp.id,
                        label: opp.opponent_name || "Unnamed opponent",
                      })
                    }
                  />
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-2 border-t border-border">
            <Button variant="ghost" size="sm" className="text-xs text-destructive" onClick={() => setDeleteTarget({ kind: "race" })}>
              <Trash2 className="w-3 h-3 mr-1" /> Delete Race
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="text-xs" onClick={onClose}>Cancel</Button>
              <Button size="sm" className="text-xs gap-1" onClick={handleSave} disabled={saving}>
                <Save className="w-3 h-3" /> {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    <DeleteConfirmDialog
      open={!!deleteTarget}
      onOpenChange={(v) => {
        if (!v) setDeleteTarget(null);
      }}
      title={deleteTarget?.kind === "opponent" ? "Delete opponent?" : "Delete race?"}
      description={
        deleteTarget?.kind === "opponent"
          ? `Remove “${deleteTarget.label}” from this race. This cannot be undone.`
          : `Permanently delete this race (${raceLabel}). This cannot be undone.`
      }
      confirmText="Delete"
      onConfirm={async () => {
        if (!deleteTarget || !race?.id) return;
        if (deleteTarget.kind === "race") {
          await onDelete(race.id);
          return;
        }
        await handleDeleteOpponent(deleteTarget.id);
      }}
    />
    </>
  );
}